import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { echo } from '@laravel/echo-vue'
import { captureException } from '@sentry/vue'
import { reject as _reject, forEach } from 'lodash'
import moment from 'moment'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import AuthService from '@/services/retailer/AuthService.js'
import MembershipService from '@/services/retailer/MembershipService.js'
import UserService from '@/services/retailer/UserService.js'
import i18n from '@/i18n/i18n.js'
import { EchoManager } from '@/utilities/echo-manager.js'
import { errorMessage, sma256Hash } from '@/utilities/helpers.js'
import { NOTIFICATION_SEVERITY_TYPES, NOTIFICATION_TYPES } from '@/constants/constants.js'
import { AUTH_ERROR_MESSAGES } from '@/constants/errorMessages.js'
import { RETAILER_TYPE } from '@/constants/retailerType.js'
import { SALES_CHANNEL_TYPE } from '@/constants/salesChannels.js'

const { t } = i18n.global

export const useAuthStore = defineStore('retailerAuth', () => {
  const notificationStore = useNotificationStore()

  const user = ref(null)
  const token = ref(null)
  const support = ref(null)
  const unread_notifications = ref([])
  const unseen_notification_count = ref('0')
  const filter_notifications_by_severities = ref([])
  const show_notification_container = ref(false)
  const nextRoute = ref(null)

  // Getters
  const getToken = computed(() => {
    return token.value ? token.value : localStorage.getItem('retailer_access_token')
  })

  const isDemo = computed(() => {
    return user.value?.retailer?.is_demo
  })

  const retailer = computed(() => user.value?.retailer)

  /**
   * The retailers this user may work at, exactly as the auth payload reported them.
   *
   * The key is absent while the multi retailer membership rollout is off, which is what keeps the
   * panel switch invisible for everyone the rollout has not reached yet.
   */
  const memberships = computed(() => user.value?.memberships ?? [])

  const isConsumerGoodsRetailer = computed(() => user.value?.retailer?.type === RETAILER_TYPE.CONSUMER_GOODS)

  const forwardSalesChannelStatus = computed(() =>
    user.value?.retailer?.sales_channels?.some(sales_channel => sales_channel.id === SALES_CHANNEL_TYPE.FORWARD_SALE)
  )

  const virtualPosSalesChannelStatus = computed(() =>
    user.value?.retailer?.sales_channels?.some(sales_channel => sales_channel.id === SALES_CHANNEL_TYPE.VIRTUAL_POS)
  )

  const carSalesSalesChannelStatus = computed(() =>
    user.value?.retailer?.sales_channels?.some(sales_channel => sales_channel.id === SALES_CHANNEL_TYPE.CAR_SALES)
  )

  const proSalesChannelsStatus = computed(() =>
    user.value?.retailer?.sales_channels?.some(sales_channel =>
      [SALES_CHANNEL_TYPE.DIRECT_CASH, SALES_CHANNEL_TYPE.DIRECT_FORWARD].includes(sales_channel.id)
    )
  )

  const getNextRoute = computed(() => {
    return nextRoute.value
  })

  // Actions
  const setToken = newToken => {
    token.value = newToken
    localStorage.setItem('retailer_access_token', newToken)
    EchoManager.updateToken(newToken)
  }

  // Sync in-memory token/user from localStorage to recover from cross-tab logins before getUser hits 401.
  const syncFromStorage = () => {
    const localToken = localStorage.getItem('retailer_access_token')
    if (localToken && localToken !== token.value) {
      token.value = localToken
      user.value = null
      EchoManager.updateToken(localToken)
      return true
    }
    return false
  }

  let isLoggingOut = false
  let isSessionExpired = false

  const handleSessionExpired = () => {
    if (isLoggingOut || isSessionExpired) return
    isSessionExpired = true

    notificationStore.add({
      type: 'info',
      message: t('shared.auth_errors.session_expired')
    })

    token.value = null
    localStorage.removeItem('retailer_access_token')
    try {
      unsubscribeFromChannel()
    } catch (e) {
      captureException(e, {
        tags: {
          auth_flow: 'session_expired',
          panel: 'retailer',
          action: 'unsubscribe_channel'
        }
      })
    }
    EchoManager.clearToken()
    window.location.href = '/login'
  }

  const logout = path => {
    if (isLoggingOut) return
    isLoggingOut = true

    if (path) {
      localStorage.setItem('next_route_retailer', path)
    }

    AuthService.logout()
      .catch(() => {
        token.value = null
        localStorage.removeItem('retailer_access_token')
      })
      .finally(() => {
        token.value = null
        localStorage.removeItem('retailer_access_token')
        try {
          unsubscribeFromChannel()
        } catch (err) {
          /* user may not be loaded */
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.userMayNotBeLoaded}: ${errorMessage(err)}`
          }
          notificationStore.add(notification)
        }
        EchoManager.clearToken()
        location.reload()
      })
  }

  /**
   * Move this session to the given retailer.
   *
   * Only the token behind this request moves, so the user's other devices stay where they were. The
   * caller reloads the app afterwards rather than patching the store: the sidebar, the route guards
   * and all forty odd retailer stores were filled from the previous retailer and none of them can
   * reset itself.
   */
  const switchRetailer = retailerId => {
    return MembershipService.switchRetailer(retailerId)
  }

  const setUser = userData => {
    user.value = userData
    support.value = {
      region_manager: userData.retailer.region_manager,
      specialist: userData.retailer.specialist,
      support_number: userData.retailer.support_number,
      operation_specialist_number: userData.retailer.operation_specialist_number
    }
    unread_notifications.value = userData.unread_notifications
  }

  const setLatestPriceDate = date => {
    user.value.latest_price_date = date
  }

  const setNextRoute = path => {
    nextRoute.value = path
  }

  const setShowNotificationContainer = value => {
    show_notification_container.value = value
  }

  const getUser = async (refresh = false) => {
    if (user.value && !refresh) {
      user.value.hash_email = await sma256Hash(user.value.email)
      return user.value
    }
    return new Promise((resolve, reject) => {
      AuthService.getUser()
        .then(async response => {
          response.data.hash_email = await sma256Hash(response.data.email)
          setUser(response.data)
          subscribeToChannel()
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.getUserError} ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const login = userData => {
    return new Promise((resolve, reject) => {
      AuthService.login(userData)
        .then(async response => {
          response.user.hash_email = await sma256Hash(response.user.email)
          setToken(response.access_token)
          setUser(response.user)
          subscribeToChannel()
          resolve(response.user)
        })
        .catch(() => {
          const notification = {
            type: 'danger',
            message: AUTH_ERROR_MESSAGES.loginError
          }
          notificationStore.add(notification)
          delete localStorage.clear()
          reject(AUTH_ERROR_MESSAGES.loginError)
        })
    })
  }

  const register = retailerData => {
    return new Promise((resolve, reject) => {
      AuthService.register(retailerData)
        .then(() => {
          return login({
            email: retailerData.email,
            password: retailerData.password
          }).then(r => {
            resolve(r)
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.registerError} ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const saveActivities = data => {
    return new Promise((resolve, reject) => {
      AuthService.saveActivities(data)
        .then(() => {
          resolve(true)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.registerError} ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const forgotPassword = retailerData => {
    return new Promise((resolve, reject) => {
      AuthService.forgotPassword(retailerData)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          const notification = {
            type: 'danger',
            message: AUTH_ERROR_MESSAGES.forgotPasswordError
          }
          notificationStore.add(notification)
          reject()
        })
    })
  }

  const resetPassword = retailerData => {
    return new Promise((resolve, reject) => {
      AuthService.resetPassword(retailerData)
        .then(response => {
          resolve(response)
        })
        .catch(() => {
          const notification = {
            type: 'danger',
            message: AUTH_ERROR_MESSAGES.resetPasswordError
          }
          notificationStore.add(notification)
          reject()
        })
    })
  }

  const getNotifications = () => {
    return new Promise((resolve, reject) => {
      UserService.getNotifications(user.value.id)
        .then(response => {
          unread_notifications.value = response.data
          unseen_notification_count.value = response.meta.unseen_notification_count
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.getUserError} ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const filterNotificationsBySeverities = () => {
    return new Promise((resolve, reject) => {
      UserService.filterNotificationsBySeverities(user.value.id)
        .then(response => {
          filter_notifications_by_severities.value = response.data
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const markAsReadNotification = id => {
    return UserService.markAsReadNotification(user.value.id, id).then(() => {
      unread_notifications.value = _reject(unread_notifications.value, item => {
        return item.id === id
      })
    })
  }

  const markAsSeenNotifications = () => {
    UserService.markAsSeenNotifications(user.value.id).then(() => {
      unseen_notification_count.value = 0
    })
  }

  const addNotification = notification => {
    const content = {
      data: {},
      created_at: moment()
    }
    forEach(notification, (n, k) => {
      if (k === 'id' || k === 'type') {
        content[k] = n
      } else {
        content.data[k] = n
      }
    })
    unread_notifications.value.unshift(content)
    unseen_notification_count.value++
  }

  const acceptPolicy = () => {
    return new Promise((resolve, reject) => {
      AuthService.acceptPolicy()
        .then(response => {
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${AUTH_ERROR_MESSAGES.policyError} ${errorMessage(err)}`
          }
          notificationStore.add(notification)
          reject(err)
        })
    })
  }

  const setUserNotifications = notifications => {
    unread_notifications.value = notifications
  }

  const subscribeToChannel = () => {
    const notificationDefaults = {
      severity: NOTIFICATION_SEVERITY_TYPES.INFO,
      is_dismissible: true,
      type: NOTIFICATION_TYPES.TOAST
    }

    const echoInstance = echo()
    echoInstance.private(`App.Models.RetailerUser.${user.value.id}`).notification(notification => {
      const notificationContent = {
        ...notificationDefaults,
        ...notification.data,
        id: notification.id,
        is_system_notification: typeof notification.data?.kind !== 'undefined'
      }

      notificationStore.add(notificationContent)
      addNotification(notification.data)
    })
  }

  const unsubscribeFromChannel = () => {
    const echoInstance = echo()
    echoInstance.leave(`App.Models.RetailerUser.${user.value.id}`)
  }

  return {
    user,
    token,
    support,
    unread_notifications,
    unseen_notification_count,
    filter_notifications_by_severities,
    show_notification_container,
    nextRoute,
    getToken,
    isDemo,
    retailer,
    memberships,
    isConsumerGoodsRetailer,
    forwardSalesChannelStatus,
    virtualPosSalesChannelStatus,
    carSalesSalesChannelStatus,
    proSalesChannelsStatus,
    getNextRoute,
    setToken,
    syncFromStorage,
    logout,
    switchRetailer,
    handleSessionExpired,
    setUser,
    setLatestPriceDate,
    setNextRoute,
    setShowNotificationContainer,
    getUser,
    login,
    register,
    saveActivities,
    forgotPassword,
    resetPassword,
    getNotifications,
    filterNotificationsBySeverities,
    markAsReadNotification,
    markAsSeenNotifications,
    addNotification,
    acceptPolicy,
    setUserNotifications,
    subscribeToChannel,
    unsubscribeFromChannel
  }
})
